import type { OffsetPageDTO } from "@/shared/contracts/base";
import type { IssuerBankAccountsByCurrency } from "./issuer-bank-accounts";

export type DocumentType = "quotation" | "invoice" | "receipt";
export type DocumentStatus =
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "cancelled";

/** ຕັ້ງຄ່າຜູ້ອອກເອກະສານ (ແຖວດຽວ — ໃຊ້ທົ່ວທຸກເອກະສານ) */
export type QuotationInvoiceIssuerSettingsDTO = {
  companyName: string;
  companyAddress: string;
  companyContact: string;
  /** MinIO key ຫຼື path — ຮູບລາຍເຊັນ */
  signatureImageKey: string | null;
  /** MinIO key ຫຼື path — ຕຣາປະທັບບໍລິສັດ */
  companyStampKey: string | null;
  /** MinIO key ຫຼື path — ໂລໂກບໍລິສັດ */
  companyLogoKey: string | null;
  /** ເລກບັນຊີ / ຊື່ບັນຊີ ຕາມສະກຸນເງິນ */
  bankAccounts: IssuerBankAccountsByCurrency;
  updatedAt: string | null;
};

export type UpdateIssuerSettingsInput = {
  companyName: string;
  companyAddress: string;
  companyContact: string;
  signatureImageKey: string | null;
  companyStampKey: string | null;
  companyLogoKey: string | null;
  bankAccounts: IssuerBankAccountsByCurrency;
};

export type QuotationInvoiceDocumentListItem = {
  id: string;
  /** quotation | invoice | receipt (PostgreSQL enum) */
  documentType: DocumentType;
  /** Human-readable document type name from DB; use for display when present */
  documentTypeName?: string | null;
  documentNumber: string | null;
  status: DocumentStatus;
  customerId: string | null;
  customerName: string;
  customerAddress: string | null;
  customerContact: string | null;
  issueDate: string | null;
  dueDate: string | null;
  paidAt: string | null;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  total: number;
  /** ອັດຕາມັດຈຳ (0–1, ເຊັ່ນ 0.3 = 30%) */
  depositPercent: number;
  /** ISO 4217 — ສະແດງຍອດເງິນ */
  currency: string;
  paymentTerms: string | null;
  notes: string | null;
  quotationId: string | null;
  invoiceId: string | null;
  /** ແມ່ແບບຈາກ Template Center (ສຳລັບພິມ/PDF) */
  templateId?: string | null;
  templateName?: string | null;
  /** ສຳລັບ receipt: payment ທີ່ໃຊ້ອອກໃບຮັບເງິນ */
  paymentId?: string | null;
  /** ສຳລັບ receipt: ຍອດຕ້ນສະບັບຈາກ invoice parent (ໃຊ້ສະແດງ summary ໃນໃບຮັບ) */
  invoiceSubtotal?: number | null;
  invoiceDiscountAmount?: number | null;
  invoiceTaxAmount?: number | null;
  invoiceTotal?: number | null;
  /** ສຳລັບ receipt: ອັດຕາມັດຈຳຈາກ invoice parent */
  invoiceDepositPercent?: number | null;
  /** ສະແດງລາຍເຊັນ/ຕຣາປະທັບຈາກການຕັ້ງຄ່າຜູ້ອອກເອກະສານ */
  showSignature: boolean;
  /** HQ branch สำหรับ stock scope (Phase 11a) */
  stockBranchId?: string | null;
  /** Linked sales order from Q→SO→I (Phase 11d) */
  salesOrderId?: string | null;
  isActive: boolean;
  /** ຜູ້ສ້າງ/ອອກເອກະສານ (ອ້າງອີງ user) */
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DocumentsListResult =
  OffsetPageDTO<QuotationInvoiceDocumentListItem>;

/** ສະຫຼຸບຕາມແຕ່ລະສະກຸນ (LAK / THB / USD) */
export type DocumentsSummaryByCurrency = {
  currency: string;
  /** Quotation issued / partially_paid — ຍອດຂໍ້ສະເໜີລວມ */
  totalQuotationsAmount: number;
  totalQuotationsCount: number;
  /** Invoice issued / partially_paid — ຈຳນວນໃບ (ງວດຕ່າງໆ ຕໍ່ 1 Quotation ໄດ້) */
  pendingInvoicesCount: number;
  /** ຍອດຄ້າງຊຳລະລວມ: sum(max(0, invoice.total − net ຈາກການຊຳລະ)) ຕໍ່ໃບ */
  pendingInvoicesOutstandingAmount: number;
  /** Receipt ທຸກສະຖານະ (ບໍ່ກອງ status) — ຍອດຕາມໃບຮັບ */
  paidReceiptsAmount: number;
  paidReceiptsCount: number;
};

export type DocumentsSummaryResult = {
  byCurrency: DocumentsSummaryByCurrency[];
};

export type QuotationInvoiceDocumentItemDTO = {
  id: string;
  documentId: string;
  orderIndex: number;
  description: string;
  /** รายละเอียดเพิ่มเติมต่อบรรทัด (ถ้าไม่มีเป็น null) */
  detail: string | null;
  quantity: number;
  unit: string | null;
  unitPrice: number;
  discountAmount: number;
  taxRate: number;
  lineTotal: number;
  productId?: string | null;
  rawMaterialId?: string | null;
  serviceId?: string | null;
  productCode?: string | null;
  productName?: string | null;
  serviceCode?: string | null;
  serviceName?: string | null;
};

export type DocumentHistoryAction =
  | "draft_created"
  | "issued"
  | "converted_to_invoice"
  | "converted_to_receipt"
  | "paid"
  | "cancelled"
  | "payment_recorded"
  | "payment_updated"
  | "payment_deleted";

export type QuotationInvoicePaymentKind =
  | "deposit"
  | "partial"
  | "final"
  | "refund";

export type QuotationInvoicePaymentAttachmentDTO = {
  id: string;
  paymentId: string;
  fileKey: string;
  fileName: string;
  orderIndex: number;
  createdAt: string;
};

export type QuotationInvoiceDocumentPaymentDTO = {
  id: string;
  documentId: string;
  kind: QuotationInvoicePaymentKind;
  amount: number;
  paidAt: string | null;
  method: string | null;
  notes: string | null;
  recordedBy: string;
  recordedByName?: string | null;
  createdAt: string;
  updatedAt: string;
  attachments: QuotationInvoicePaymentAttachmentDTO[];
  /** ໃບຮັບເງິນທີ່ຜູກກັບ payment ນີ້ (null = ຍັງບໍ່ໄດ້ອອກ) */
  receiptId?: string | null;
  receiptNumber?: string | null;
};

export type CreateDocumentPaymentInput = {
  kind: QuotationInvoicePaymentKind;
  amount: number;
  paidAt?: string | null;
  method?: string | null;
  notes?: string | null;
  attachments: Array<{ fileKey: string; fileName: string }>;
};

export type UpdateDocumentPaymentInput = {
  kind?: QuotationInvoicePaymentKind;
  amount?: number;
  paidAt?: string | null;
  method?: string | null;
  notes?: string | null;
  attachments?: Array<{ fileKey: string; fileName: string }>;
};

export type QuotationInvoiceDocumentHistoryEntryDTO = {
  id: string;
  documentId: string;
  action: DocumentHistoryAction;
  performedBy: string;
  performedAt: string;
  notes: string | null;
  relatedDocumentId: string | null;
  performedByName?: string | null;
};

export type QuotationInvoiceHeadroomDTO = {
  quotationTotal: number;
  invoicedTotal: number;
  remaining: number;
};

export type LinkedSalesOrderDTO = {
  id: string;
  orderNo: string;
  status: string;
};

export type DocumentDetailResult = {
  document: QuotationInvoiceDocumentListItem;
  items: QuotationInvoiceDocumentItemDTO[];
  history: QuotationInvoiceDocumentHistoryEntryDTO[];
  payments: QuotationInvoiceDocumentPaymentDTO[];
  /** Invoices with `quotation_id` = this document (only when `document` is a quotation). */
  linkedInvoices: QuotationInvoiceDocumentListItem[];
  /** Sales order created from this quotation (Phase 11c). */
  linkedSalesOrder?: LinkedSalesOrderDTO | null;
  /** ຍອດເຫຼືອສຳລັບງວດໃບບິນ — ມີເມື່ອເປັນ quotation */
  quotationInvoiceHeadroom: QuotationInvoiceHeadroomDTO | null;
};

// Create document
export type CreateDocumentItemDTO = {
  description?: string;
  detail?: string | null;
  quantity: number;
  unit?: string | null;
  unitPrice: number;
  discountAmount?: number;
  taxRate?: number;
  productId?: string | null;
  rawMaterialId?: string | null;
  serviceId?: string | null;
};

/** Body ສຳລັບ POST convert quotation → invoice (ງວດໃໝ່) */
export type ConvertQuotationToInvoiceBody = {
  items: CreateDocumentItemDTO[];
  notes?: string | null;
  discountAmount?: number;
  taxRate?: number;
};

export type CreateDocumentDTO = {
  documentType: DocumentType;
  status: "draft" | "issued";
  documentNumber?: string | null;
  customerId?: string | null;
  customerName: string;
  customerAddress?: string | null;
  customerContact?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  /** ISO 4217 (default USD) */
  currency?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
  items: CreateDocumentItemDTO[];
  /** Document-level discount amount */
  discountAmount?: number;
  /** Document-level tax rate (e.g. 0.1 for 10%) */
  taxRate?: number;
  /** Document-level deposit rate (e.g. 0.3 for 30%) */
  depositPercent?: number;
  /** ແມ່ແບບຈາກ Template Center (ທາງເລືອກ) */
  templateId?: string | null;
  /** ສະແດງລາຍເຊັນ & ຕຣາປະທັບໃນເອກະສານ (ຄ່າເລີ່ມຕົ້ນ: ມີ) */
  showSignature?: boolean;
  /** HQ branch สำหรับ stock scope (Phase 11a) */
  stockBranchId?: string | null;
};

/** ຄ່າ query `categoryId` — ກອງເຉພາະລູກຄ້າທີ່ຍັງບໍ່ໄດ້ໃສ່ຫມວດຫມູ່ */
export const QUOTATION_INVOICE_CUSTOMER_UNCATEGORIZED = "__none__" as const;

/** ຄ່າປ່ຽນ UI ສຳລັບການກອງ — ບໍ່ສົ່ງ categoryId ໄປ API */
export const QUOTATION_INVOICE_CUSTOMER_FILTER_ALL = "__all__" as const;

export type QuotationInvoiceCustomerCategoryListItem = {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
};

export type QuotationInvoiceCustomerListItem = {
  id: string;
  name: string;
  address: string | null;
  contact: string | null;
  taxId: string | null;
  categoryId: string | null;
  /** ຊື່ຫມວດຫມູ່ຈາກ join (null ຖ້າບໍ່ມີ) */
  categoryName: string | null;
  isActive: boolean;
};

export type QuotationInvoiceDocumentTypeListItem = {
  id: string;
  code: string;
  name: string;
  orderIndex: number;
  isActive: boolean;
};

// Audit history list (cross-document)
export type HistoryListEntryDTO = {
  id: string;
  documentId: string;
  documentNumber: string | null;
  documentType: string;
  customerName: string;
  action: DocumentHistoryAction;
  performedBy: string;
  performedByName: string | null;
  performedAt: string;
  notes: string | null;
  relatedDocumentId: string | null;
};

export type HistoryListResult = {
  data: HistoryListEntryDTO[];
  total: number;
};

export type HistorySummaryResult = {
  last24hCount: number;
  totalDocumentsCount: number;
};

export type MyDocumentsStatusSummary = {
  count: number;
  total: number;
};

export type MyDocumentsTypeSummary = {
  quotationIssued: MyDocumentsStatusSummary;
  invoiceOpen: {
    count: number;
    outstandingTotal: number;
  };
  receiptAll: MyDocumentsStatusSummary;
};

export type MyDocumentsSummaryByCurrency = {
  currency: string;
  draft: MyDocumentsStatusSummary;
  byDocumentType: MyDocumentsTypeSummary;
};

export type MyDocumentsSummary = {
  draft: MyDocumentsStatusSummary;
  issued: MyDocumentsStatusSummary;
  partiallyPaid: MyDocumentsStatusSummary;
  paid: MyDocumentsStatusSummary;
  cancelled: MyDocumentsStatusSummary;
  byDocumentType: MyDocumentsTypeSummary;
  byCurrency: MyDocumentsSummaryByCurrency[];
};
